import * as React from "react"
import { FaGoogle, FaTwitter } from "react-icons/fa"

import { useSignInWithEmailAndPassword, useSignInWithGoogle, useSignInWithTwitter } from "react-firebase-hooks/auth"
import { auth, getFirebaseAnalytics } from "../services/firebase"
import { useForm } from "react-hook-form"

import { useRouter } from "next/router"
import LogoIcon from "./brand/LogoIcon"
import { UserCredential, createUserWithEmailAndPassword } from "firebase/auth"
import { dateIsLessThanSecondsAgo } from "../utils/dates"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { STRIPE_APPSUMO_PRICE_ID, usePaymentsData } from "../hooks/usePaymentsData"
import Button from "@mui/joy/Button"
import TextField from "@mui/joy/TextField"
import Typography from "@mui/joy/Typography"
import Sheet from "@mui/joy/Sheet"
import { useBrowserType } from "../hooks/useBrowserType"
import { redeemReferralCode } from "../services/firebase/functions"
import Input from "@mui/joy/Input"
import {
    ANNUAL_PRICE_ID,
    ANNUAL_PRICE_ID_19USD,
    MONTHLY_PRICE_ID,
    MONTHLY_PRICE_ID_19USD,
    PROMO_CODES
} from "../services/stripe"
import { useFeatureIsOn } from "@growthbook/growthbook-react"
import { useAnalytics } from "../hooks/useAnalytics"

interface ILoginProps {
    title?: string
    subtitle?: string
    defaultSigningUp?: boolean
    postLogoElement?: React.ReactNode
    customSuccessMessage?: string
    blockRedirect?: boolean
}
const Separator = () => (
    <div className="flex opacity-50 items-center">
        <div className="bg-white w-full h-px mx-2" />
        <span>or</span>
        <div className="bg-white  w-full h-px mx-2" />
    </div>
)
export default function Login({
    title,
    subtitle,
    defaultSigningUp,
    postLogoElement,
    blockRedirect,
    customSuccessMessage,
    ...rest
}: ILoginProps) {
    const router = useRouter()
    const annualBilling = router.query?.billing === "annual"
    // @ts-ignore
    const forcePlan: "free" | "basic" | "appsumotwitvid" | undefined = router.query?.plan

    const [signInWithGoogle, googleUser, googleLoading, googleError] = useSignInWithGoogle(auth)
    const [signInWithTwitter, twitterUser, twitterLoading, twitterError] = useSignInWithTwitter(auth)

    // todo: make validation work
    const { register, formState, handleSubmit } = useForm()
    const [signInWithEmailAndPassword, emailUser, emailLoading, error] = useSignInWithEmailAndPassword(auth)
    const { startCheckoutSession, basicPrice, loadingCheckout, annualPrice, appsumoPrice } = usePaymentsData()
    const [signingUp, setSigningUp] = useState(defaultSigningUp || false)

    useEffect(() => {
        setSigningUp(defaultSigningUp || false)
    }, [defaultSigningUp])
    const [customLoading, setCustomLoading] = useState(false)
    const loading = emailLoading || googleLoading || twitterLoading || customLoading || loadingCheckout

    const stripePromoCode = router.query.promo_code as string | undefined
    const usdPricing19Enabled = useFeatureIsOn("19usd_mo_pricing")

    const { logEvent } = useAnalytics()

    const signInAndRedirect = async (fn: any, method: string) => {
        const userCredentials: UserCredential = await fn()

        if (!userCredentials) {
            toast.error("Couldn't sign in with the chosen method... Please reload and try again")
            return
        }
        if (
            userCredentials.user?.metadata?.creationTime &&
            dateIsLessThanSecondsAgo(new Date(userCredentials.user.metadata.creationTime), 20)
        ) {
            // user just signed up

            logEvent("sign_up", { method, user_id: auth.currentUser?.uid })

            // claim referral
            const localStorageReferral = localStorage.getItem("referral")
            const referral = router.query.referral || localStorageReferral
            if (referral && referral !== "subscribe") {
                try {
                    await redeemReferralCode({ referralCode: referral as string })
                } catch (error) {
                    console.error(error)
                }
            }

            // user got here by clicking on a pricing plan in the pricing page, redirect straight to stripe
            if (forcePlan === "basic") {
                if (annualBilling) {
                    await startCheckoutSession(usdPricing19Enabled ? ANNUAL_PRICE_ID_19USD : ANNUAL_PRICE_ID)
                    toast.loading("Redirecting to Stripe...")
                    return
                } else {
                    await startCheckoutSession(usdPricing19Enabled ? MONTHLY_PRICE_ID_19USD : MONTHLY_PRICE_ID)
                    toast.loading("Redirecting to Stripe...")
                }
            } else if (forcePlan === "appsumotwitvid") {
                toast.loading("Redirecting to Stripe...")
                await startCheckoutSession(appsumoPrice?.id || STRIPE_APPSUMO_PRICE_ID, true)
            } else if (!blockRedirect) {
                window.location.assign(`/videos/create?signedUp=true`)

                // basicPrice?.id && (await startCheckoutSession(basicPrice?.id))
            }
        } else {
            logEvent("sign_in", { method, user_id: auth.currentUser?.uid })

            if (!blockRedirect) {
                router.push(`/videos/create`)
            }
        }
    }

    const signInEmail = () => {
        handleSubmit(async ({ email, password }) => {
            setCustomLoading(true)
            try {
                const userCred = await signInWithEmailAndPassword(email, password)
                if (!userCred) {
                    throw new Error(
                        "Couldn't sign in with the given credentials... Please reload or try a different password"
                    )
                }
                if (userCred?.user) {
                    if (!blockRedirect) {
                        router.push(`/videos/create`)
                    }
                }
            } catch (error) {
                console.log(error)
                error?.toString && toast.error(error.toString())
            }
            setCustomLoading(false)
        })()
    }
    const signUpEmail = () => {
        handleSubmit(async ({ email, password }) => {
            setCustomLoading(true)

            try {
                await toast.promise(createUserWithEmailAndPassword(auth, email, password), {
                    loading: "Creating account...",
                    success: customSuccessMessage || "Welcome to Twitvid!",
                    error: "Please reload the page and try again"
                })
                logEvent("sign_up", { method: "email", user_id: auth.currentUser?.uid })

                // claim referral
                const localStorageReferral = localStorage.getItem("referral")
                const referral = router.query.referral || localStorageReferral
                if (referral) {
                    try {
                        await redeemReferralCode({ referralCode: referral as string })
                    } catch (error) {
                        console.error(`Couldn't redeem referral code ${referral}`)
                    }
                }

                // user got here by clicking on a pricing plan in the pricing page, redirect straight to stripe
                if (forcePlan === "basic") {
                    if (annualBilling) {
                        toast.loading("Redirecting to Stripe...")
                        await startCheckoutSession(usdPricing19Enabled ? ANNUAL_PRICE_ID_19USD : ANNUAL_PRICE_ID)

                        return
                    } else {
                        toast.loading("Redirecting to Stripe...")
                        await startCheckoutSession(usdPricing19Enabled ? MONTHLY_PRICE_ID_19USD : MONTHLY_PRICE_ID)
                    }
                } else if (forcePlan === "appsumotwitvid") {
                    toast.loading("Redirecting to Stripe...")
                    await startCheckoutSession(appsumoPrice?.id || STRIPE_APPSUMO_PRICE_ID, true)
                } else if (!blockRedirect) {
                    // router.push(`/videos/create?signedUp=true`)
                    window.location.assign(`/videos/create?signedUp=true`)
                    // if (basicPrice.id) {
                    //     await startCheckoutSession(basicPrice.id)
                    // }
                    // toast.loading("Redirecting to payment form...")
                }

                // @ts-ignore
                // await startCheckoutSession(basicPrice.id)
                // toast.loading("Redirecting to Stripe...")
            } catch (error) {
                console.error(`Error signing up with email: ${error}`)
                if (error.code === "auth/email-already-in-use") {
                    toast.error("Email already in use")
                } else {
                    error?.toString && toast.error("Something went wrong... Please reload try again")
                }
            }
            setCustomLoading(false)
        })()
    }
    const { isEmbeddedBrowser } = useBrowserType()

    const [redeemingCode, setRedeemingCode] = useState(false)
    const [referralCode, setReferralCode] = useState("")

    return (
        <div className="flex flex-col items-center justify-center text-white w-full md:w-96 p-4 md:p-0">
            <div className="flex items-center mb-8">
                <LogoIcon className=" fill-white" />
                {postLogoElement}
            </div>
            <div className="flex flex-col justify-center align-center w-full">
                <div className="text-white font-semibold text-3xl mb-3 text-center">
                    {stripePromoCode && Object.values(PROMO_CODES).includes(stripePromoCode)
                        ? "Redeem your special offer!"
                        : redeemingCode
                        ? "Redeem code"
                        : title || (signingUp ? "Welcome!" : "Log in to your account")}
                </div>
                {!redeemingCode && (
                    <>
                        <p className="text-center text-white mb-8">
                            {subtitle ||
                                (PROMO_CODES["smma3months"] === stripePromoCode
                                    ? "Sign in or sign up to get 3 months free"
                                    : PROMO_CODES["smma1month"] === stripePromoCode
                                    ? "Sign in or sign up to get 1 month free"
                                    : signingUp
                                    ? "Create an account to continue"
                                    : "Welcome back! Please login to continue.")}
                            {!stripePromoCode && !signingUp && (
                                <div
                                    className="opacity-70  text-sm m-auto -mb-4 mt-1 cursor-pointer hover:underline "
                                    onClick={() => setRedeemingCode(true)}
                                >
                                    Redeem a promo code
                                </div>
                            )}
                        </p>
                    </>
                )}
            </div>
            {redeemingCode ? (
                <Sheet
                    sx={{
                        maxWidth: 450,
                        width: "100%",
                        mx: "auto", // margin left & right

                        // px: 2, // padding left & right
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        borderRadius: "sm",
                        backgroundColor: "transparent",
                        border: "none",
                        boxShadow: "none",
                        color: "white"
                    }}
                    variant="outlined"
                >
                    <Input
                        placeholder="Enter your referral code"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value)}
                    />
                    <Button
                        sx={{ mt: 1 /* margin top */, backgroundColor: "transparent !important", color: "white" }}
                        loading={loading}
                        variant="outlined"
                        onClick={async () => {
                            router.push(`/invite/${referralCode}?appsumo=true`)
                        }}
                        disabled={!referralCode}
                    >
                        Redeem
                    </Button>
                </Sheet>
            ) : (
                <Sheet
                    sx={{
                        maxWidth: 450,
                        width: "100%",
                        mx: "auto", // margin left & right

                        // px: 2, // padding left & right
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        borderRadius: "sm",
                        backgroundColor: "transparent",
                        border: "none",
                        boxShadow: "none",
                        color: "white"
                    }}
                    variant="outlined"
                >
                    <TextField
                        // html input attribute

                        type="email"
                        error={!!formState.errors.email || !!error}
                        helperText={error ? "Wrong email or password" : ""}
                        placeholder="johndoe@email.com"
                        className=""
                        {...register("email", { required: true, pattern: /^\S+@\S+$/i })}
                        label="Email"
                        sx={{
                            label: {
                                color: "white"
                            },
                            helperText: {
                                color: "white"
                            }
                        }}
                    />

                    <TextField
                        error={!!formState.errors.password || !!error}
                        type="password"
                        placeholder="●●●●●●●●●●"
                        autoComplete="password"
                        label="Password"
                        {...register("password", { required: true })}
                        sx={{
                            label: {
                                color: "white"
                            }
                        }}
                    />
                    <Button
                        sx={{ mt: 1 /* margin top */, backgroundColor: "white", color: "black" }}
                        loading={loading}
                        variant="outlined"
                        disabled={!formState.isValid}
                        className="bg-transparent  disabled:opacity-50 border cursor-pointer hover:bg-transparent border-white text-white  bg-opacity-95 font-semibold text-base "
                        onClick={() => (signingUp ? signUpEmail() : signInEmail())}
                    >
                        <span className="text-black text-base">{signingUp ? "Sign up" : "Log in"}</span>
                    </Button>
                    <Separator />
                    <div className="flex flex-col w-full space-y-4 text-gray-900">
                        {!isEmbeddedBrowser && (
                            <button
                                onClick={() => signInAndRedirect(signInWithGoogle, "google")}
                                className="hover:bg-gray-300 transition-colors duration-500 bg-blue-50 text-gray-900 font-semibold border border-gray-300 flex flex-row items-center justify-center rounded-lg py-2 gap-x-4"
                            >
                                <FaGoogle />
                                Continue with Google
                            </button>
                        )}
                        <button
                            onClick={() => signInAndRedirect(signInWithTwitter, "twitter")}
                            className="hover:bg-gray-300 transition-colors duration-500 bg-blue-50 text-gray-900 font-semibold border border-gray-200 flex flex-row items-center justify-center rounded-lg py-2 gap-x-4"
                        >
                            <FaTwitter />
                            Continue with Twitter
                        </button>
                    </div>

                    <Typography
                        endDecorator={
                            <span
                                className="text-white cursor-pointer  font-semibold"
                                onClick={() => setSigningUp(!signingUp)}
                            >
                                {signingUp ? "Log in" : "Sign up"}
                            </span>
                        }
                        fontSize="sm"
                        sx={{ alignSelf: "center" }}
                    >
                        <span className="text-white opacity-70">
                            {signingUp ? "Already got an account?" : "Don't have an account?"}
                        </span>
                    </Typography>
                </Sheet>
            )}
        </div>
    )
}
